import { motion } from 'framer-motion';

export default function HomeMadeMeals() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dblue text-center mb-12">
              Home Made Meals
            </h1>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              At Harbourside Children’s Centre, we are
              committed to fostering healthy eating habits
              and providing well-balanced, nutritious meals
              (breakfast, lunch, and afternoon snack!) for all
              children in our care. We believe that
              mealtimes should be a positive, social
              experience where children can enjoy
              delicious food in a warm and welcoming
              environment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3 space-y-12">
              


              {/* Chef Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <h2 className="text-3xl font-bold text-dblue mb-6">Our Wonderful Chef</h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Our dedicated in-house chef, Chef Luchie,
                prepares fresh meals each day – following a weekly
                menu for our breakfasts, lunches, and afternoon
                snacks. We provide milk with breakfast and lunch,
                and filtered water is always available throughout
                the day.
                </p>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">10</div>
                      <div className="text-sm text-gray-600">Week Menu Rotation</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">24/7</div>
                      <div className="text-sm text-gray-600">Filtered Water Available</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Philosophy Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h2 className="text-3xl font-bold text-dblue mb-8">Nutritional Licensing Regulations</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <p className="text-gray-700 text-lg">All meals are peanut-free and meet the nutritional
standards set by Vancouver Coastal Health,
ensuring that every child receives high-quality,
balanced meals to support their growth and
development</p>
                  </div>
                </div>
              </motion.div>

              {/* Nutrition Details Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <h2 className="text-3xl font-bold text-dblue mb-8">Nutrition Details</h2>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                    <h3 className="text-lg font-semibold text-green-800 mb-4">Brightwheel</h3>
                    <ul className="space-y-2 text-gray-700">
                    Families will receive the next week’s menu every
                    Friday on Brightwheel, allowing parents to see what
                    their children are eating at the Centre. We value
                    open communication with families and are happy
                    to share recipes!
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                    <h3 className="text-lg font-semibold text-blue-800 mb-4">Health Standards</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>✓ Children receive healthy food and drinks
                      according to Canada’s Food Guide</li>
                      <li>✓ Nutritional needs are met based on a child’s age, hours of care, preferences, and cultural background</li>
                      <li>✓ Children are not fed by propped bottles, forced to eat, or left unsupervised during meals.</li>
                      <li> ✓ Food is never used as a reward or
                      punishment.</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
                  <p className="text-gray-700 leading-relaxed">
                    We aim to be as accommodating as possible to children's food allergies, restrictions, and special diets. 
                    We believe in working with families to ensure that children have healthy and satisfying meals 
                    despite their food requirements.
                  </p>
                </div>
              </motion.div>

            </div>

            {/* Sidebar with Images */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="sticky top-8 space-y-6"
              >
                <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-2xl p-4">
                  <img 
                    src="/images/kitchenphoto.jpg" 
                    alt="Our kitchen where fresh meals are prepared"
                    className="w-full h-48 object-cover rounded-lg shadow-md"
                  />
                  <p className="text-sm text-gray-600 mt-2 text-center">Our kitchen where fresh meals are prepared</p>
                </div>
                <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-2xl p-4">
                  <img 
                    src="/images/gallery/ricemeal.jpg" 
                    alt="Hearty rice meal served fresh"
                    className="w-full h-48 object-cover rounded-lg shadow-md"
                  />
                  <p className="text-sm text-gray-600 mt-2 text-center">Delicious Chicken and Veggie Rice</p>
                </div>
                <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-2xl p-4">
                  <img 
                    src="/images/gallery/veggieplates.jpg" 
                    alt="Colorful veggie plates made in-house"
                    className="w-full h-48 object-cover rounded-lg shadow-md"
                  />
                  <p className="text-sm text-gray-600 mt-2 text-center">Colorful veggie plates made in-house</p>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
